// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AiPicks.js
// Admin: AI Picks - the AI's ranking of an ASX morning's positive
// announcements, most likely to rise from the open to the close first.
// Rankings come from ai_picks.py (jobs side): live runs every minute
// 07:00-09:56 Sydney while announcements arrive, backfills started here.
// Data: /api/admin/picks (picks.py). A day shows its newest done run with the
// live model unless another run is chosen. Today's page refreshes every minute.
// Top N, minimum market cap and minimum traded value are applied here, on the
// full ranking: candidates that fail a filter drop out and the next ones move
// up - no re-ranking needed. Market cap is as at the previous close, and
// approximate (see picks.py).
// Setup (model, reasoning, minimum rating), the picks prompt (versions, as the
// AI Prompts page) and backfill sit at the foot of the page.
// Layout and admin guard follow AdminAudit.js; the API is @admin_required.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px' },
  section: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, marginBottom: '16px' },
  row:   { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
  label: { fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' },
  input: {
    padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)',
    color: 'var(--text)', outline: 'none',
  },
  btn: {
    padding: '7px 16px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif",
    background: 'var(--accent)', color: 'var(--on-accent)',
  },
  mono:  { fontFamily: "'JetBrains Mono', monospace" },
  muted: { color: 'var(--text-muted)', fontSize: '13px' },
  err:   { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  ok:    { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
  card: {
    border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px',
    marginBottom: '12px', background: 'var(--bg)',
  },
  cardHead: { display: 'flex', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: '6px' },
  rank: { ...{ fontFamily: "'JetBrains Mono', monospace" }, fontSize: '20px', fontWeight: 600, color: 'var(--accent)', minWidth: '28px' },
  tag: {
    display: 'inline-block', fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', padding: '1px 6px',
    borderRadius: 'var(--radius)', border: '1px solid var(--border)', color: 'var(--text-muted)', marginRight: '6px',
  },
  ann:   { fontSize: '12px', marginTop: '6px', paddingLeft: '40px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)',
    color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase',
  },
  td:   { padding: '8px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  area: {
    width: '100%', minHeight: '260px', boxSizing: 'border-box', resize: 'vertical',
    fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', lineHeight: 1.5, padding: '12px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)',
    color: 'var(--text)', outline: 'none',
  },
  link: { color: 'var(--accent)', cursor: 'pointer' },
};

const REFRESH_MS = 60 * 1000;
const sydToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });   // YYYY-MM-DD
const fmtT  = iso => new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const fmtD  = iso => new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
const aud   = v => v == null ? '—' : v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}k`;
const pct   = v => (v > 0 ? '+' : '') + v.toFixed(2) + '%';
const col   = v => v > 0 ? 'var(--success)' : v < 0 ? 'var(--error)' : 'var(--text-muted)';
const num   = v => (v === '' || v == null ? null : Number(v));

function Field({ label, children }) {
  return <div><div style={s.label}>{label}</div>{children}</div>;
}

function PickCard({ p, n }) {
  return (
    <div style={{ ...s.card, borderLeft: `3px solid ${p.pct != null ? col(p.pct) : 'var(--accent)'}` }}>
      <div style={s.cardHead}>
        <span style={s.rank}>{n}</span>
        <Link to={`/company?ticker=${p.symbol}:AU`} style={{ ...s.mono, fontWeight: 600, fontSize: '15px', color: 'var(--text)' }}>{p.symbol}</Link>
        <span style={s.muted}>{p.name}</span>
        <span style={{ ...s.muted, ...s.mono, marginLeft: 'auto' }}>cap {aud(p.mcap)} · value {aud(p.avg_value)}/day</span>
        {p.pct != null && (
          <span style={{ ...s.mono, fontWeight: 600, color: col(p.pct) }}>
            ${p.open.toFixed(3)} → ${p.close.toFixed(3)} {pct(p.pct)}
          </span>
        )}
      </div>
      <div style={{ fontSize: '13px', paddingLeft: '40px' }}>{p.reason}</div>
      {p.announcements.map(a => (
        <div key={a.key} style={s.ann}>
          <span style={s.tag}>{a.significance}/5</span>
          {a.novelty && <span style={s.tag}>{a.novelty}</span>}
          {(a.thematics || []).map(t => <span key={t} style={s.tag}>{t}</span>)}
          <span style={{ ...s.muted, fontSize: '12px', marginRight: '6px' }}>{fmtT(a.announced_at)}</span>
          {a.url ? <a href={a.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{a.headline}</a> : a.headline}
          <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{a.summary}</div>
        </div>
      ))}
    </div>
  );
}

function Setup({ data, models, onSaved }) {
  const live = data.history[0];
  const [model, setModel]   = useState(live?.model || '');
  const [effort, setEffort] = useState(live?.reasoning || 'high');
  const [minSig, setMinSig] = useState(String(live?.min_significance || 3));
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const m = models.find(x => x.id === model);

  const save = async () => {
    setMsg(''); setErr('');
    try {
      const r = await api.post('/admin/picks/settings', { model, reasoning: effort, min_significance: Number(minSig) });
      setMsg(r.data.message); onSaved();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Setup</div>
      <div style={s.row}>
        <Field label="OpenRouter model">
          <input style={{ ...s.input, width: '340px' }} list="picks-models" value={model} onChange={e => setModel(e.target.value)} />
          <datalist id="picks-models">{models.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</datalist>
        </Field>
        <Field label="Reasoning">
          <select style={s.input} value={effort} onChange={e => setEffort(e.target.value)}>
            {data.reasoning.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Min rating">
          <input style={{ ...s.input, width: '70px' }} type="number" min="1" max="5" value={minSig} onChange={e => setMinSig(e.target.value)} />
        </Field>
        <button style={s.btn} onClick={save}>Save</button>
      </div>
      <div style={{ ...s.muted, marginTop: '8px' }}>
        {m ? `${m.name}: $${m.prompt.toFixed(2)} in / $${m.completion.toFixed(2)} out per million tokens${m.reasoning ? '' : ' - no reasoning support'}. ` : ''}
        {live && `Live since ${fmtT(live.created_at)}${live.created_by ? ` by ${live.created_by}` : ''}.`}
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>{err}</div>}
    </div>
  );
}

function PromptEditor({ versions, onSaved }) {
  const [text, setText] = useState(versions[0]?.body || '');
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  useEffect(() => { setText(versions[0]?.body || ''); }, [versions]);

  const save = async () => {
    setMsg(''); setErr('');
    try {
      const r = await api.post('/admin/picks/prompt', { body: text });
      setMsg(r.data.message); onSaved();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Ranking prompt</div>
      <div style={{ ...s.muted, marginBottom: '12px' }}>
        Guidance for the ranking. The candidates, their history, prices and indicators, and the output format are added by the code.
        Saving adds a new version and makes it live.
      </div>
      <textarea style={s.area} value={text} onChange={e => setText(e.target.value)} />
      <div style={{ ...s.row, marginTop: '10px' }}>
        <button style={{ ...s.btn, opacity: text.trim() && text !== versions[0]?.body ? 1 : 0.5 }}
                disabled={!text.trim() || text === versions[0]?.body} onClick={save}>Save as new version</button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>{err}</div>}
      {versions.length > 1 && (
        <details style={{ marginTop: '12px', fontSize: '12px' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Earlier versions ({versions.length - 1})</summary>
          {versions.slice(1).map(v => (
            <div key={v.created_at} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span>{fmtT(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ''}</span>
              <span style={s.link} onClick={() => setText(v.body)}>Load into editor</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

function Backfill() {
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');
  const [redo, setRedo] = useState(false);
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');

  const start = async () => {
    setMsg(''); setErr('');
    try {
      const r = await api.post('/admin/picks/backfill', { from, to, redo });
      setMsg(r.data.message);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Backfill</div>
      <div style={{ ...s.muted, marginBottom: '12px' }}>
        Ranks past sessions with the live setup, from what existed before each day's open. Days already ranked with the
        live model are skipped unless "Redo" is ticked (a redo adds a new run; the old one is kept).
      </div>
      <div style={s.row}>
        <Field label="From"><input style={s.input} type="date" value={from} onChange={e => setFrom(e.target.value)} /></Field>
        <Field label="To"><input style={s.input} type="date" value={to} onChange={e => setTo(e.target.value)} /></Field>
        <label style={{ ...s.muted, display: 'flex', gap: '6px', alignItems: 'center', paddingBottom: '8px' }}>
          <input type="checkbox" checked={redo} onChange={e => setRedo(e.target.checked)} /> Redo
        </label>
        <button style={{ ...s.btn, opacity: from && to ? 1 : 0.5 }} disabled={!from || !to} onClick={start}>Start</button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>{err}</div>}
    </div>
  );
}

export default function AiPicks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate]   = useState('');
  const [runId, setRunId] = useState(null);
  const [data, setData]   = useState(null);
  const [setup, setSetup] = useState(null);
  const [models, setModels] = useState([]);
  const [err, setErr]     = useState('');
  const [topN, setTopN]   = useState('5');
  const [minCap, setMinCap] = useState('');        // A$ millions
  const [minVal, setMinVal] = useState('');        // A$ thousands a day

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const q = new URLSearchParams({ ...(date ? { date } : {}), ...(runId ? { run: runId } : {}) });
      const r = await api.get(`/admin/picks/day?${q}`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  }, [date, runId]);

  const loadSetup = useCallback(async () => {
    try { setSetup((await api.get('/admin/picks/settings')).data); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSetup();
    api.get('/admin/audit/models').then(r => setModels(r.data.models)).catch(() => {});
  }, [loadSetup]);

  // Today's ranking changes as announcements arrive.
  useEffect(() => {
    if (!data || data.date !== sydToday()) return;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [data, load]);

  const run = data?.runs.find(r => r.run_id === data.run_id);
  const { shown, rest } = useMemo(() => {
    const picks = data?.picks || [];
    const cap = num(minCap), val = num(minVal);
    const pass = p => (cap == null || (p.mcap != null && p.mcap >= cap * 1e6))
                   && (val == null || (p.avg_value != null && p.avg_value >= val * 1e3));
    const ok = picks.filter(pass);
    const n = Math.max(1, Number(topN) || 5);
    return { shown: ok.slice(0, n), rest: picks.map(p => ({ ...p, out: !pass(p), top: ok.slice(0, n).includes(p) })) };
  }, [data, topN, minCap, minVal]);
  const done = shown.filter(p => p.pct != null);

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />
      <div style={s.content}>
        <div style={s.heading}>AI Picks</div>
        <div style={s.sub}>
          The AI's ranking of the ASX morning's positive announcements, most likely to rise from the open to the close first.
          Live rankings update every minute from 7:00 to 9:56am Sydney time as announcements arrive.
        </div>
        {err && <div style={s.err}>✕ {err}</div>}

        <div style={s.section}>
          <div style={s.row}>
            <Field label="Session">
              <select style={s.input} value={data?.date || ''} onChange={e => { setRunId(null); setDate(e.target.value); }}>
                {data?.date && !data.dates.includes(data.date) && <option value={data.date}>{fmtD(data.date)}</option>}
                {(data?.dates || []).map(d => <option key={d} value={d}>{fmtD(d)}</option>)}
              </select>
            </Field>
            <Field label="Run">
              <select style={s.input} value={data?.run_id || ''} onChange={e => setRunId(Number(e.target.value))}>
                {(data?.runs || []).map(r => (
                  <option key={r.run_id} value={r.run_id} disabled={r.status !== 'done'}>
                    {fmtT(r.created_at)} · {r.mode} · {r.model.split('/').pop()} · {r.status}{r.candidates ? ` · ${r.candidates} cand.` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Top N"><input style={{ ...s.input, width: '70px' }} type="number" min="1" value={topN} onChange={e => setTopN(e.target.value)} /></Field>
            <Field label="Min market cap (A$M)"><input style={{ ...s.input, width: '110px' }} type="number" min="0" value={minCap} onChange={e => setMinCap(e.target.value)} /></Field>
            <Field label="Min traded value (A$k/day)"><input style={{ ...s.input, width: '110px' }} type="number" min="0" value={minVal} onChange={e => setMinVal(e.target.value)} /></Field>
            <button style={s.btn} onClick={load}>Refresh</button>
          </div>
          {run && (
            <div style={{ ...s.muted, marginTop: '10px' }}>
              {run.mode === 'live' ? 'Live' : 'Backfill'} run {fmtT(run.finished_at || run.created_at)} · {run.model} ({run.reasoning}) ·
              rating {run.min_significance}+ · {run.candidates} candidates
              {run.tokens_in ? ` · ${run.tokens_in.toLocaleString()} in / ${run.tokens_out.toLocaleString()} out` : ''}
              {run.model !== data.live_model && ' · not the live model'}
            </div>
          )}
        </div>

        {data && !data.picks.length && <div style={{ ...s.section, ...s.muted }}>No ranking for this session yet.</div>}

        {shown.length > 0 && (
          <div style={s.section}>
            <div style={{ ...s.sectionTitle, display: 'flex', gap: '16px', alignItems: 'baseline' }}>
              <span>Top {shown.length}</span>
              {done.length > 0 && (
                <span style={{ ...s.mono, fontSize: '13px', fontWeight: 500, color: col(done.reduce((t, p) => t + p.pct, 0)) }}>
                  avg {pct(done.reduce((t, p) => t + p.pct, 0) / done.length)} open → close · {done.filter(p => p.pct > 0).length}/{done.length} up
                </span>
              )}
            </div>
            {shown.map((p, i) => <PickCard key={p.symbol} p={p} n={i + 1} />)}
          </div>
        )}

        {rest.length > 0 && (
          <div style={s.section}>
            <details>
              <summary style={{ ...s.sectionTitle, cursor: 'pointer', marginBottom: 0 }}>Full ranking ({rest.length})</summary>
              <table style={{ ...s.table, marginTop: '12px' }}>
                <thead>
                  <tr>
                    <th style={s.th}>AI rank</th><th style={s.th}>Stock</th><th style={s.th}>Reason</th>
                    <th style={s.th}>Cap</th><th style={s.th}>Value/day</th><th style={s.th}>Open → close</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map(p => (
                    <tr key={p.symbol} style={{ opacity: p.out ? 0.45 : 1, background: p.top ? 'var(--bg)' : 'transparent' }}>
                      <td style={{ ...s.td, ...s.mono }}>{p.rank}</td>
                      <td style={{ ...s.td, ...s.mono, fontWeight: 600 }}>
                        <Link to={`/company?ticker=${p.symbol}:AU`} style={{ color: 'var(--text)' }}>{p.symbol}</Link>
                        {p.out && <div style={{ ...s.muted, fontSize: '11px', fontWeight: 400 }}>filtered out</div>}
                      </td>
                      <td style={{ ...s.td, fontSize: '12px' }}>{p.reason}</td>
                      <td style={{ ...s.td, ...s.mono }}>{aud(p.mcap)}</td>
                      <td style={{ ...s.td, ...s.mono }}>{aud(p.avg_value)}</td>
                      <td style={{ ...s.td, ...s.mono, color: p.pct != null ? col(p.pct) : 'var(--text-muted)' }}>
                        {p.pct != null ? pct(p.pct) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        )}

        {setup && <Setup data={setup} models={models} onSaved={loadSetup} />}
        {setup && <PromptEditor versions={setup.prompts} onSaved={loadSetup} />}
        <Backfill />
      </div>
    </div>
  );
}
